import styled from "@emotion/styled";
import assertNever from "assert-never";
import React from "react";
import { match } from "ts-pattern";
import { TIMES_SVG } from "../../utils/svgs";
import { AppIcon } from "../Generic/AppIcon";
import Flex from "../Generic/Flex";
import { isDefined } from "../Generic/IsDefined";
import Spinner from "../Generic/Spinner";
import { default as AlertCircle } from "/public/assets/alert_circle.svg?react";
import { default as CheckCircle } from "/public/assets/check_circle.svg?react";
import { default as TimesCircle } from "/public/assets/times_circle.svg?react";

export type ToastVariant = "success" | "warning" | "error" | "neutral" | "primary";

const StyledToast = styled.div<{ variant: ToastVariant }>`
  background-color: ${(props: { variant: ToastVariant }) => getThemeColor(props.variant)};
  color: ${(props: { variant: ToastVariant }) => (props.variant === "neutral" ? "black" : "white")};
  box-shadow: 0px 10px 15px -3px rgba(0, 0, 0, 0.1), 0px 4px 6px -2px rgba(0, 0, 0, 0.05);
  border-radius: 8px;
  padding-left: 12px;
  transition: all 250ms ease;
  min-height: 50px;
  box-sizing: border-box;
  display: flex;
`;

export interface ToastProps {
  variant: ToastVariant;
  message: React.ReactNode;
  isLoading: boolean;
  onRequestClose?: () => void;
}

const getThemeColor = (variant: ToastVariant): string => {
  return match(variant)
    .with("success", () => "var(--success-color)")
    .with("warning", () => "var(--warning-color)")
    .with("error", () => "var(--danger-color)")
    .with("neutral", () => "white")
    .with("primary", () => "var(--primary-color)")
    .exhaustive();
};

const getCloseIconColor = (variant: ToastVariant): string => {
  return match(variant)
    .with("success", () => "white")
    .with("warning", () => "white")
    .with("error", () => "white")
    .with("neutral", () => "var(--gray-500)")
    .with("primary", () => "white")
    .exhaustive();
};

const CloseIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
`;

const Toast = React.forwardRef((props: ToastProps, ref: React.ForwardedRef<HTMLDivElement>) => {
  return (
    <StyledToast variant={props.variant} ref={ref}>
      <Flex direction="row" alignItems="center" justifyContent="space-between" flex={1}>
        <Flex direction="row" alignItems="center" gap={8}>
          {(() => {
            if (props.isLoading) {
              return <Spinner size={20} />;
            }

            if (props.variant === "neutral") {
              return <></>;
            }

            return (
              <Flex>
                {(() => {
                  switch (props.variant) {
                    case "primary":
                      return <AlertCircle />;
                    case "success":
                      return <CheckCircle />;
                    case "error":
                      return <TimesCircle />;
                    case "warning":
                      return <AlertCircle />;
                    default:
                      assertNever(props.variant);
                  }
                })()}
              </Flex>
            );
          })()}
          <p>{props.message}</p>
        </Flex>
        {isDefined(props.onRequestClose) ? (
          <CloseIconWrapper onClick={props.onRequestClose}>
            <AppIcon size={20} icon={TIMES_SVG} stroke={getCloseIconColor(props.variant)} />
          </CloseIconWrapper>
        ) : null}
      </Flex>
    </StyledToast>
  );
});

export default Toast;
